'use client';
'use no memo';

import React, { useEffect, useRef, useState } from 'react';

import { currencyToApplePayConfig } from '@kit/shared/currency';

interface HyperpaySuccessData {
  id?: string;
  resourcePath?: string;
  [key: string]: unknown;
}

export interface HyperpayCheckoutFormProps {
  checkoutId: string;
  integrity?: string;
  baseUrl?: string;
  onSuccess?: (data: HyperpaySuccessData) => void;
  onError?: (error: Error) => void;
  className?: string;
  showApplePay?: boolean;
  /**
   * Merchant name shown on the Apple Pay sheet (the "PAY <label>" line).
   * Optional — falls back to the current hostname when not supplied so existing
   * call sites keep working without changes.
   */
  displayName?: string;
  /**
   * Checkout currency (ISO 4217). Drives the Apple Pay sheet's
   * `supportedCountries` and `supportedNetworks`, which must match the merchant
   * account — a mismatch makes the sheet open and immediately dismiss. Defaults
   * to SAR so existing Saudi call sites keep their current behaviour.
   */
  currency?: string;
}

interface WpwlOptions {
  style?: string;
  locale?: string;
  showLabels?: boolean;
  showPlaceholders?: boolean;
  brandDetection?: boolean;
  showCVVHint?: boolean;
  paymentTarget?: string;
  [key: string]: unknown;
}

declare global {
  interface Window {
    wpwlOptions?: WpwlOptions;
  }
}

export function HyperpayCheckoutForm({
  checkoutId,
  integrity,
  baseUrl = 'https://eu-test.oppwa.com',
  onSuccess,
  onError,
  className = '',
  showApplePay = true,
  displayName,
  currency = 'JOD',
}: HyperpayCheckoutFormProps) {
  const formRef = useRef<HTMLFormElement>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const scriptRef = useRef<HTMLScriptElement | null>(null);

  useEffect(() => {
    if (!checkoutId) {
      setError('No checkout ID provided');
      setIsLoading(false);
      return;
    }

    // Configure wpwlOptions before loading the script
    window.wpwlOptions = {
      style: 'plain',
      locale: 'en',
      showLabels: false,
      showPlaceholders: true,
      brandDetection: true,
      showCVVHint: true,
      paymentTarget: '_top', // Required for 3D Secure
      registrations: {
        hideCheckbox: true,
        requireCvv: true
      },
      placeholders: {
        cardNumber: 'Card number',
        cardHolder: 'Name',
        expiryDate: 'MM / YY',
        cvv: 'CVV'
      },
      iframeStyles: {
        'card-number-placeholder': {
          'color': '#9ca3af',
          'font-size': '16px',
          'font-family': 'Arial, sans-serif',
          'line-height': '24px'
        },
        'cvv-placeholder': {
          'color': '#9ca3af',
          'font-size': '16px',
          'font-family': 'Arial, sans-serif',
          'line-height': '24px'
        },
        ':focus': {
          'color': '#111827'
        },
        'input': {
          'line-height': '24px',
          'height': '24px'
        }
      },
      onReady: function() {
        setIsLoading(false);
        console.log('Hyperpay widget ready');
        
        // Apply customizations after widget loads
        setTimeout(function() {
          // Hide duplicate card forms
          const cardForms = document.querySelectorAll('.wpwl-form-card');
          if (cardForms.length > 1) {
            for (let i = 1; i < cardForms.length; i++) {
              const form = cardForms[i] as HTMLElement;
              if (form) {
                form.style.display = 'none';
              }
            }
          }
          
          // Hide brand selector
          const brandGroups = document.querySelectorAll('.wpwl-group-brand');
          brandGroups.forEach(function(group) {
            const element = group as HTMLElement;
            if (element) {
              element.style.display = 'none';
            }
          });
          
          // Update button text
          const payButtons = document.querySelectorAll('.wpwl-button-pay');
          payButtons.forEach(function(button) {
            button.textContent = 'Pay now';
          });
          
          // Add placeholders
          const placeholders = {
            '.wpwl-control-cardNumber': 'Card number',
            '.wpwl-control-expiry': 'MM / YY',
            '.wpwl-control-cardHolder': 'Name',
            '.wpwl-control-cvv': 'CVV'
          };
          
          Object.entries(placeholders).forEach(([selector, placeholder]) => {
            const elements = document.querySelectorAll(selector);
            elements.forEach(el => {
              if (el instanceof HTMLInputElement) {
                el.placeholder = placeholder;
                el.setAttribute('placeholder', placeholder);
              }
              // Handle iframes by setting data attributes that CSS can use
              if (el instanceof HTMLIFrameElement) {
                el.setAttribute('data-placeholder', placeholder);
                el.title = placeholder;
              }
            });
          });

          // Fix tab order to match visual order
          function setTabOrder() {
            const tabOrder = [
              '.wpwl-control-cardNumber',
              '.wpwl-control-cardHolder', 
              '.wpwl-control-expiry',
              '.wpwl-control-cvv',
              '.wpwl-button-pay'
            ];
            
            tabOrder.forEach((selector, index) => {
              const element = document.querySelector(selector) as HTMLElement;
              if (element) {
                element.tabIndex = index + 2; // Start from 2, close button is 1
              }
            });
          }
          
          // Set tab order with retries for dynamic content
          setTabOrder();
          setTimeout(setTabOrder, 300);
        }, 100);
      },
      onBeforeSubmitCard: function() {
        return true;
      },
      // COPYandPAY surfaces server/validation errors here — including Apple Pay
      // merchant-validation failures relayed from HyperPay. This is the closest
      // the browser gets to "the response from HyperPay" for the open-and-close
      // symptom; the raw validation POST is also visible in the Network tab
      // (a call to the OPPWA host at the moment the sheet opens).
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      onError: function (error: any) {
        console.error('[HyperPay] widget onError ←', {
          name: error?.name,
          message: error?.message,
          errorCode: error?.errorCode ?? error?.code,
          detail: error,
        });
      },
    };

    // Apple Pay inside the COPYandPAY widget. The widget wraps the Apple Pay JS
    // API and performs merchant validation server-side using the certificates
    // uploaded to HyperPay, so we implement no validateMerchant here.
    // currencyCode / total.amount are intentionally omitted: the widget inherits
    // them from the server-side checkout (amount + currency).
    //
    // supportedCountries / supportedNetworks are NOT inherited from the checkout
    // — they are sheet-level constraints passed straight through to
    // ApplePaySession, and Apple validates them against the merchant account.
    // Requesting a country or scheme the merchant doesn't support (e.g. mada,
    // which is Saudi-only, for a Jordanian merchant) makes the sheet open and
    // then immediately dismiss. Both are therefore derived from the checkout
    // currency rather than hardcoded to the Saudi set.
    if (showApplePay && window.wpwlOptions) {
      const applePayLabel =
        displayName?.trim() ||
        (typeof window !== 'undefined' ? window.location.hostname : 'Payment');

      const { supportedCountries, supportedNetworks } =
        currencyToApplePayConfig(currency);

      window.wpwlOptions.applePay = {
        displayName: applePayLabel,
        total: { label: applePayLabel },
        supportedNetworks,
        supportedCountries
      };

      // → REQUEST TO APPLE/HYPERPAY: the exact sheet constraints we hand the
      // widget. If supportedCountries/networks don't match what the HyperPay
      // entity supports, Apple opens the sheet and immediately dismisses it —
      // so this line + the entity log server-side pinpoint that mismatch.
      // Also report what THIS browser can actually do, so we can tell a
      // config problem from an unsupported-device/no-card problem.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const applePaySession = (window as any).ApplePaySession;
      console.log('[HyperPay] Apple Pay config → requesting', {
        currency,
        supportedCountries,
        supportedNetworks,
        baseUrl,
        checkoutId,
        applePaySessionPresent: !!applePaySession,
        supportsVersion5:
          applePaySession?.supportsVersion?.(5) ?? 'n/a',
        canMakePayments: applePaySession?.canMakePayments?.() ?? 'n/a',
      });
    }

    // Create and append the widget script
    const script = document.createElement('script');
    script.src = `${baseUrl}/v1/paymentWidgets.js?checkoutId=${checkoutId}`;
    script.async = true;
    
    if (integrity) {
      script.integrity = integrity;
      script.crossOrigin = 'anonymous';
    }

    script.onload = () => {
      console.log('HyperPay script loaded successfully');
    };

    script.onerror = () => {
      console.error('Failed to load HyperPay script');
      setError('Failed to load payment form. Please refresh and try again.');
      setIsLoading(false);
      if (onError) {
        onError(new Error('Failed to load Hyperpay widget'));
      }
    };

    scriptRef.current = script;
    document.body.appendChild(script);

    // Cleanup
    return () => {
      // Only clean up when component unmounts, not on re-renders
      if (scriptRef.current && scriptRef.current.parentNode) {
        scriptRef.current.parentNode.removeChild(scriptRef.current);
      }
      scriptRef.current = null;
      delete window.wpwlOptions;
    };
    // currency/displayName feed the Apple Pay sheet config built above, so a
    // change must rebuild wpwlOptions and reload the widget script.
  }, [checkoutId, currency, displayName]);

  // Handle the payment result from URL parameters
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const resourcePath = urlParams.get('resourcePath');
    
    if (resourcePath && onSuccess) {
      // Payment completed, notify parent
      onSuccess({ resourcePath });
    }
  }, [onSuccess]);

  // Apple Pay shows as an additional COPYandPAY "brand", listed first so its
  // button renders above the card form. On non-Apple / non-Safari devices the
  // widget simply omits it and shows only the card brands.
  const cardBrands = 'VISA MASTER AMEX MADA';
  const dataBrands = showApplePay ? `APPLEPAY ${cardBrands}` : cardBrands;

  return (
    <div className={`hyperpay-checkout-container ${className}`}>
      {isLoading && (
        <div className="flex items-center justify-center p-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
        </div>
      )}
      
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      )}
      
      {/* Payment form container - action URL will be shopperResultUrl from checkout */}
      <form
        ref={formRef}
        className="paymentWidgets"
        data-brands={dataBrands}
      />
      
      <style>{`
        /* HyperPay COPYandPAY form styling */
        
        /* Form container */
        .wpwl-container {
          color: #374151;
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
          -webkit-font-smoothing: antialiased;
          -moz-osx-font-smoothing: grayscale;
        }
        
        /* Reorder form fields using flexbox */
        .wpwl-form-card {
          display: flex !important;
          flex-wrap: wrap !important;
        }
        
        /* Set order for fields */
        .wpwl-group-cardNumber {
          order: 1 !important;
          width: 100% !important;
        }
        
        .wpwl-group-cardHolder {
          order: 2 !important;
          width: 100% !important;
        }
        
        .wpwl-group-expiry {
          order: 3 !important;
          width: 100% !important;
        }
        
        .wpwl-group-cvv {
          order: 4 !important;
          width: 100% !important;
        }
        
        .wpwl-group-submit {
          order: 5 !important;
          width: 100% !important;
        }
        
        /* Ensure plain style has no extra styling */
        .wpwl-form {
          background: transparent;
          border: none;
          box-shadow: none;
          padding: 0;
          margin: 0;
        }
        
        /* Hide brand selector */
        .wpwl-group-brand {
          display: none !important;
        }
        
        /* Form groups */
        .wpwl-group {
          margin-bottom: 16px;
          padding: 0 !important;
          width: 100% !important;
          position: relative;
        }
        
        /* Icons for each field */
        .wpwl-group-cardNumber::before {
          content: '';
          position: absolute;
          left: 14px;
          top: 50%;
          transform: translateY(-50%);
          width: 20px;
          height: 20px;
          background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%236b7280'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z'/%3E%3C/svg%3E");
          background-size: contain;
          background-repeat: no-repeat;
          z-index: 10;
          pointer-events: none;
        }
        
        .wpwl-group-cardHolder::before {
          content: '';
          position: absolute;
          left: 14px;
          top: 50%;
          transform: translateY(-50%);
          width: 20px;
          height: 20px;
          background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%236b7280'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z'/%3E%3C/svg%3E");
          background-size: contain;
          background-repeat: no-repeat;
          z-index: 10;
          pointer-events: none;
        }
        
        .wpwl-group-expiry::before {
          content: '';
          position: absolute;
          left: 14px;
          top: 50%;
          transform: translateY(-50%);
          width: 20px;
          height: 20px;
          background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%236b7280'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z'/%3E%3C/svg%3E");
          background-size: contain;
          background-repeat: no-repeat;
          z-index: 10;
          pointer-events: none;
        }
        
        .wpwl-group-cvv::before {
          content: '';
          position: absolute;
          left: 14px;
          top: 50%;
          transform: translateY(-50%);
          width: 20px;
          height: 20px;
          background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%236b7280'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z'/%3E%3C/svg%3E");
          background-size: contain;
          background-repeat: no-repeat;
          z-index: 10;
          pointer-events: none;
        }
        
        /* All input controls */
        .wpwl-control,
        .wpwl-control-cardNumber,
        .wpwl-control-cardHolder,
        .wpwl-control-expiry,
        .wpwl-control-cvv {
          width: 100% !important;
          padding: 14px 24px 14px 48px !important;
          border: 1px solid #e5e7eb !important;
          border-radius: 8px;
          font-size: 16px !important;
          font-family: Arial, Helvetica, sans-serif;
          color: #111827;
          background-color: #ffffff;
          box-sizing: border-box !important;
          transition: all 0.2s;
          -webkit-font-smoothing: antialiased;
          -moz-osx-font-smoothing: grayscale;
          height: 48px !important;
          line-height: normal !important;
          margin: 0 !important;
        }
        
        .wpwl-control:focus,
        .wpwl-control-cardNumber:focus,
        .wpwl-control-cardHolder:focus,
        .wpwl-control-expiry:focus,
        .wpwl-control-cvv:focus {
          outline: none;
          border-color: #6b7280;
          box-shadow: 0 0 0 3px rgba(107, 114, 128, 0.1);
        }
        
        /* Placeholder styling */
        .wpwl-control::placeholder,
        .wpwl-control-cardNumber::placeholder,
        .wpwl-control-cardHolder::placeholder,
        .wpwl-control-expiry::placeholder,
        .wpwl-control-cvv::placeholder {
          color: #9ca3af;
          opacity: 1;
        }
        
        /* Submit button */
        .wpwl-button,
        .wpwl-button-pay {
          width: 100%;
          padding: 14px 24px;
          background-color: #111827;
          color: white;
          border: none;
          border-radius: 8px;
          font-size: 16px;
          font-weight: 500;
          cursor: pointer;
          transition: background-color 0.2s;
          margin-top: 20px;
        }
        
        .wpwl-button:hover,
        .wpwl-button-pay:hover {
          background-color: #1f2937;
        }
        
        .wpwl-button:disabled,
        .wpwl-button-pay:disabled {
          background-color: #e5e7eb;
          color: #9ca3af;
          cursor: not-allowed;
        }
        
        /* Hide registration checkbox */
        .wpwl-group-registration {
          display: none !important;
        }
        
        /* Error messages */
        .wpwl-has-error .wpwl-control,
        .wpwl-has-error .wpwl-control-cardNumber,
        .wpwl-has-error .wpwl-control-cardHolder,
        .wpwl-has-error .wpwl-control-expiry,
        .wpwl-has-error .wpwl-control-cvv {
          border-color: #ef4444;
        }
        
        .wpwl-hint {
          color: #ef4444;
          font-size: 13px;
          margin-top: 4px;
        }
        
        /* Clean up spacing */
        .wpwl-form {
          margin-bottom: 0;
        }
        
        /* Hide labels if they appear */
        .wpwl-label {
          display: none !important;
        }
        
        /* Fix for gray boxes - ensure wrappers are transparent */
        .wpwl-wrapper,
        .wpwl-wrapper-cardNumber,
        .wpwl-wrapper-cardHolder,
        .wpwl-wrapper-expiry,
        .wpwl-wrapper-cvv,
        .wpwl-wrapper-brand,
        .wpwl-wrapper-submit {
          background: transparent !important;
          border: none !important;
          padding: 0 !important;
          margin: 0 !important;
          height: auto !important;
          min-height: 48px !important;
          width: 100% !important;
          box-sizing: border-box !important;
        }
        
        /* Ensure groups have proper height */
        .wpwl-group {
          min-height: 48px !important;
        }
        
        /* Ensure submit wrapper doesn't have extra styling */
        .wpwl-group-submit {
          background: transparent !important;
          border: none !important;
          padding: 0 !important;
        }
        
        /* Style iframes properly */
        iframe.wpwl-control-cardNumber,
        iframe.wpwl-control-cvv {
          background: #ffffff !important;
          border: none;
        }
        
        /* Hide HyperPay's internal request iframe */
        .wpwl-control-internalRequestIframe,
        iframe[name="internalRequest"] {
          display: none !important;
          height: 0 !important;
          visibility: hidden !important;
        }

        /* ---- Apple Pay prominence ----
           The widget renders Apple Pay in its own SIBLING container
           (.wpwl-container-applePay) after the card container, so the flex +
           order that pull it to the top must live on THEIR PARENT (this
           component's wrapper div) — flex on .wpwl-container itself only
           reorders that container's children and does nothing. */
        .hyperpay-checkout-container {
          display: flex;
          flex-direction: column;
        }

        .hyperpay-checkout-container > .wpwl-container-applePay {
          order: -1;
        }

        /* Widen the whole chain around the button — the widget's group
           wrappers shrink-wrap/float by default, which is why a width:100%
           button alone still renders at its ~100px natural size. */
        .wpwl-container-applePay,
        .wpwl-form-applePay,
        .wpwl-form-applePay .wpwl-group,
        .wpwl-form-applePay .wpwl-group-submit,
        .wpwl-form-applePay .wpwl-wrapper {
          display: block !important;
          width: 100% !important;
          margin: 0 !important;
          padding: 0 !important;
          background: transparent !important;
          border: none !important;
          float: none !important;
        }

        /* "or pay with card" divider beneath the Apple Pay button */
        .wpwl-form-applePay::after {
          content: 'or pay with card';
          display: block;
          text-align: center;
          color: #9ca3af;
          font-size: 13px;
          margin: 14px 0 2px;
        }

        /* Match the Pay now button's footprint (14px padding ×2 + 16px text
           ≈ 50px tall, 8px radius, full width). The widget renders one of:
           an <apple-pay-button> web component (sized via custom properties),
           a .wpwl-apple-pay-button div, or a .wpwl-button-applePay button —
           cover all three. */
        .hyperpay-checkout-container apple-pay-button,
        .wpwl-apple-pay-button,
        .wpwl-button-applePay {
          display: block !important;
          box-sizing: border-box !important;
          width: 100% !important;
          min-width: 100% !important;
          max-width: 100% !important;
          height: 50px !important;
          margin: 0 !important;
          float: none !important;
          border-radius: 8px !important;
          --apple-pay-button-width: 100%;
          --apple-pay-button-height: 50px;
          --apple-pay-button-border-radius: 8px;
        }
      `}</style>
    </div>
  );
}